/**
 * Frontend permission helper — mirrors public.can_edit_entry() in Postgres.
 *
 * Phase 2b (V16) — Decides whether the current user can EDIT or DELETE a row.
 *  - Super Admin                          → always
 *  - Workspace admin                      → always within their workspace
 *  - Elevated role (directeur_projet,
 *    daf, direction_generale)             → always within their workspace
 *  - Anyone else (regular contributor)    → only if they created the row
 *
 * NOTE: This is an OPTIMISTIC client-side gate to drive the UI (showing/hiding
 * Edit & Delete buttons). The real enforcement lives in Postgres RLS — even
 * if the gate is bypassed in the browser, the database will refuse mutation.
 *
 * Usage:
 *   import { canEditEntry } from '@/lib/permissions';
 *   const editable = canEditEntry({ entry, userId, profile, currentWorkspace });
 *   {editable && <Button onClick={...}><Pencil /></Button>}
 */
const ELEVATED_ROLES = new Set([
    'directeur_projet',
    'daf',
    'direction_generale',
]);

export function canEditEntry({ entry, userId, profile, currentWorkspace }) {
    if (!entry || !userId) return false;
    if (profile?.is_super_admin) return true;
    if (!currentWorkspace) return false;
    if (entry.workspace_id && entry.workspace_id !== currentWorkspace.id) return false;
    if (currentWorkspace.is_admin) return true;
    if (currentWorkspace.role && ELEVATED_ROLES.has(currentWorkspace.role)) return true;
    return entry.created_by && entry.created_by === userId;
}

export function isElevatedOrAdmin({ profile, currentWorkspace }) {
    if (profile?.is_super_admin) return true;
    if (!currentWorkspace) return false;
    if (currentWorkspace.is_admin) return true;
    return currentWorkspace.role && ELEVATED_ROLES.has(currentWorkspace.role);
}
